import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, Play, Square, Clock, Send, FileText, Dices, Swords, BookOpen, X, Share2, Sparkles, Save, Edit3, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_ICONS = {
  note: <FileText className="w-3 h-3" />,
  dice_roll: <Dices className="w-3 h-3" />,
  combat_event: <Swords className="w-3 h-3" />,
};

const ENTRY_COLORS = {
  note: "text-foreground",
  dice_roll: "text-primary",
  combat_event: "text-red-400",
};

const formatTime = (isoStr) => {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

const SessionLogPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaign, setCampaign] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("__none__");
  const [noteInput, setNoteInput] = useState("");
  const [entryType, setEntryType] = useState("note");
  const [recapLoading, setRecapLoading] = useState(false);
  const [recapEditing, setRecapEditing] = useState(false);
  const [recapDraft, setRecapDraft] = useState("");
  const entriesEndRef = useRef(null);

  const generateRecap = async () => {
    if (!activeSession) return;
    setRecapLoading(true);
    try {
      const res = await axios.post(`${API}/sessions/${activeSession.id}/recap`, {}, { withCredentials: true });
      const { recap, recap_generated_at, sources } = res.data;
      setActiveSession(prev => prev ? { ...prev, recap, recap_generated_at } : prev);
      setSessions(prev => prev.map(s => s.id === activeSession.id ? { ...s, recap, recap_generated_at } : s));
      const parts = [];
      if (sources?.dm_entries) parts.push(`${sources.dm_entries} DM entr${sources.dm_entries === 1 ? "y" : "ies"}`);
      if (sources?.player_notes) parts.push(`${sources.player_notes} player note${sources.player_notes === 1 ? "" : "s"}`);
      toast.success(`Recap generated from ${parts.join(" + ") || "session data"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate recap");
    } finally {
      setRecapLoading(false);
    }
  };

  const saveRecapEdit = async () => {
    if (!activeSession) return;
    try {
      await axios.put(`${API}/sessions/${activeSession.id}/recap`, { recap: recapDraft }, { withCredentials: true });
      setActiveSession(prev => prev ? { ...prev, recap: recapDraft, recap_generated_at: new Date().toISOString() } : prev);
      setSessions(prev => prev.map(s => s.id === activeSession.id ? { ...s, recap: recapDraft } : s));
      setRecapEditing(false);
      toast.success("Recap saved");
    } catch {
      toast.error("Failed to save recap");
    }
  };

  useEffect(() => { fetchData(); }, [campaignId]);

  // Handle deep link from ChaptersPage
  useEffect(() => {
    const chapterId = searchParams.get("chapter");
    const chapterTitle = searchParams.get("title");
    if (chapterId && chapterTitle && !loading) {
      setNewTitle(`Session: ${chapterTitle}`);
      setSelectedChapterId(chapterId);
      setCreateOpen(true);
      setSearchParams({});
    }
  }, [loading, searchParams]);

  useEffect(() => {
    entriesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeSession?.entries]);

  const fetchData = async () => {
    try {
      const [campRes, sessRes, chapRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/sessions`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/chapters`, { withCredentials: true })
      ]);
      setCampaign(campRes.data);
      setSessions(sessRes.data);
      setChapters(chapRes.data);
      if (sessRes.data.length > 0 && !activeSession) {
        const active = sessRes.data.find(s => s.is_active) || sessRes.data[0];
        setActiveSession(active);
      }
    } catch {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const refreshSession = async (id) => {
    try {
      const res = await axios.get(`${API}/sessions/${id}`, { withCredentials: true });
      setActiveSession(res.data);
      setSessions(prev => prev.map(s => s.id === id ? res.data : s));
    } catch { /* ignore */ }
  };

  const createSession = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await axios.post(`${API}/sessions`, {
        campaign_id: campaignId,
        title: newTitle,
        chapter_id: selectedChapterId === "__none__" ? null : selectedChapterId
      }, { withCredentials: true });
      toast.success("Session started!");
      setCreateOpen(false);
      setNewTitle("");
      setSelectedChapterId("__none__");
      await fetchData();
      setActiveSession(res.data);
    } catch {
      toast.error("Failed to create session");
    }
  };

  const deleteSession = async (id) => {
    const ok = await confirm("Delete Session", "Delete this session log? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/sessions/${id}`, { withCredentials: true });
      toast.success("Session deleted");
      if (activeSession?.id === id) setActiveSession(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const addEntry = async () => {
    if (!activeSession || !noteInput.trim()) return;
    try {
      await axios.post(`${API}/sessions/${activeSession.id}/entries`, {
        type: entryType,
        content: noteInput
      }, { withCredentials: true });
      setNoteInput("");
      refreshSession(activeSession.id);
    } catch {
      toast.error("Failed to add entry");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!activeSession) return;
    try {
      await axios.delete(`${API}/sessions/${activeSession.id}/entries/${entryId}`, { withCredentials: true });
      refreshSession(activeSession.id);
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  const endSession = async () => {
    if (!activeSession) return;
    try {
      await axios.post(`${API}/sessions/${activeSession.id}/end`, {}, { withCredentials: true });
      toast.success("Session ended");
      refreshSession(activeSession.id);
      fetchData();
    } catch {
      toast.error("Failed to end session");
    }
  };

  const linkChapter = async (chapterId) => {
    if (!activeSession) return;
    try {
      await axios.put(`${API}/sessions/${activeSession.id}/link-chapter?chapter_id=${chapterId}`, {}, { withCredentials: true });
      toast.success("Linked to chapter");
      refreshSession(activeSession.id);
      fetchData();
    } catch {
      toast.error("Failed to link chapter");
    }
  };

  const getChapterName = (chapterId) => {
    const ch = chapters.find(c => c.id === chapterId);
    return ch ? ch.title : "Unlinked";
  };

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="session-log-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Session Log</h1>
            <p className="text-muted-foreground text-sm">Timestamped notes linked to your campaign chapters</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="start-session-button">
                <Play className="w-4 h-4 mr-2" /> Start Session
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Start a New Session</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Session Title</label>
                  <Input
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Session 1: Into the Mines"
                    className="bg-input border-border rounded-sm"
                    data-testid="session-title-input"
                    onKeyDown={(e) => e.key === "Enter" && createSession()}
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Link to Chapter (optional)</label>
                  <Select value={selectedChapterId} onValueChange={setSelectedChapterId}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="session-chapter-select">
                      <SelectValue placeholder="No chapter" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="__none__">No chapter linked</SelectItem>
                      {chapters.map(ch => (
                        <SelectItem key={ch.id} value={ch.id}>{ch.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createSession} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="confirm-start-session">
                  Start Session
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {sessions.length === 0 ? (
          <div className="empty-state">
            <Clock className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No sessions yet</h3>
            <p className="text-muted-foreground mb-6">Start your first session to begin logging</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Session list */}
            <div className="space-y-2">
              {sessions.map(session => (
                <Card
                  key={session.id}
                  className={`bg-card border rounded-sm cursor-pointer entity-card ${
                    activeSession?.id === session.id ? 'border-primary' : 'border-border'
                  }`}
                  onClick={() => setActiveSession(session)}
                  data-testid={`session-item-${session.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {session.is_active ? (
                          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                        ) : (
                          <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className="text-foreground text-sm font-medium truncate">{session.title}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                        data-testid={`delete-session-${session.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-muted-foreground">{formatDate(session.started_at)}</span>
                      {session.shared_with_players && (
                        <Share2 className="w-3 h-3 text-primary" title="Shared with players" />
                      )}
                      {session.chapter_id && (
                        <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm truncate">
                          {getChapterName(session.chapter_id)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Active session detail */}
            <div className="lg:col-span-3">
              {activeSession ? (
                <Card className="bg-card border-border rounded-sm">
                  <CardContent className="p-6">
                    {/* Session Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                      <div>
                        <h2 className="text-xl font-serif text-foreground">{activeSession.title}</h2>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">{formatDate(activeSession.started_at)}</span>
                          {activeSession.is_active && (
                            <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-sm">LIVE</span>
                          )}
                          {activeSession.chapter_id ? (
                            <button
                              className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-sm hover:bg-primary/20"
                              onClick={() => navigate(`/campaign/${campaignId}/chapters`)}
                              data-testid="session-chapter-link"
                            >
                              <BookOpen className="w-3 h-3 inline mr-1" />
                              {getChapterName(activeSession.chapter_id)}
                            </button>
                          ) : activeSession.is_active && chapters.length > 0 && (
                            <Select value="__none__" onValueChange={(v) => { if (v !== "__none__") linkChapter(v); }}>
                              <SelectTrigger className="h-6 text-xs bg-transparent border-dashed border-muted-foreground w-auto px-2">
                                <SelectValue placeholder="Link chapter..." />
                              </SelectTrigger>
                              <SelectContent className="bg-popover border-border">
                                <SelectItem value="__none__">Link to chapter...</SelectItem>
                                {chapters.map(ch => (
                                  <SelectItem key={ch.id} value={ch.id}>{ch.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <span className="text-xs text-muted-foreground">{activeSession.entries?.length || 0} entries</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            try {
                              const res = await axios.put(`${API}/sessions/${activeSession.id}/share`, {}, { withCredentials: true });
                              const shared = res.data.shared_with_players;
                              setActiveSession(prev => ({ ...prev, shared_with_players: shared }));
                              setSessions(prev => prev.map(s => s.id === activeSession.id ? { ...s, shared_with_players: shared } : s));
                              toast.success(shared ? "Session shared with players" : "Session unshared");
                            } catch { toast.error("Failed to toggle sharing"); }
                          }}
                          variant={activeSession.shared_with_players ? "default" : "outline"}
                          size="sm"
                          className={activeSession.shared_with_players ? "bg-primary text-primary-foreground rounded-sm" : "border-border rounded-sm"}
                          data-testid="share-session-button"
                        >
                          <Share2 className="w-3 h-3 mr-1" />
                          {activeSession.shared_with_players ? "Shared" : "Share"}
                        </Button>
                        {activeSession.is_active && (
                          <Button onClick={endSession} variant="outline" size="sm" className="border-destructive text-destructive rounded-sm" data-testid="end-session-button">
                            <Square className="w-3 h-3 mr-1" /> End Session
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* AI Recap */}
                    {(activeSession.recap || recapEditing) ? (
                      <div className="mb-4 bg-primary/5 border border-primary/40 rounded-md p-3" data-testid="session-recap-block">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-primary" />
                            <span className="text-[11px] font-bold text-primary uppercase tracking-wider">Session Recap</span>
                            {activeSession.recap_generated_at && !recapEditing && (
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(activeSession.recap_generated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {recapEditing ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => setRecapEditing(false)} className="h-6 text-[10px]" data-testid="recap-cancel"><X className="w-3 h-3" /></Button>
                                <Button size="sm" onClick={saveRecapEdit} className="h-6 text-[10px] bg-primary text-primary-foreground" data-testid="recap-save"><Check className="w-3 h-3 mr-1" />Save</Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={generateRecap} disabled={recapLoading} className="h-6 text-[10px]" title="Regenerate from session data" data-testid="recap-regenerate">
                                  <Sparkles className="w-3 h-3 mr-1" />{recapLoading ? "Generating…" : "Regenerate"}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setRecapDraft(activeSession.recap || ""); setRecapEditing(true); }} className="h-6 text-[10px]" data-testid="recap-edit"><Edit3 className="w-3 h-3" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                        {recapEditing ? (
                          <textarea
                            value={recapDraft}
                            onChange={(e) => setRecapDraft(e.target.value)}
                            className="w-full h-40 text-sm bg-background border border-border rounded-sm p-2 text-foreground/90 resize-y focus:outline-none focus:border-primary"
                            data-testid="recap-edit-input"
                          />
                        ) : (
                          <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap" data-testid="recap-text">{activeSession.recap}</p>
                        )}
                        {!recapEditing && activeSession.shared_with_players && (
                          <p className="text-[10px] text-primary mt-2 italic">Visible to players (session is shared)</p>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4 bg-muted/20 border border-dashed border-border rounded-md p-3 flex items-center justify-between" data-testid="session-recap-empty">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-foreground">No recap yet</p>
                            <p className="text-[11px] text-muted-foreground leading-snug">Generate one from your DM entries{activeSession.entries?.length === 0 ? " (add some first)" : ""} and any non-private character session-notes from this session.</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={generateRecap}
                          disabled={recapLoading || (activeSession.entries?.length || 0) === 0}
                          className="bg-primary text-primary-foreground rounded-sm flex-shrink-0"
                          data-testid="generate-recap-button"
                        >
                          <Sparkles className="w-3 h-3 mr-1" />{recapLoading ? "Generating…" : "Generate Recap"}
                        </Button>
                      </div>
                    )}

                    {/* Entries */}
                    <div className="space-y-1 max-h-[50vh] overflow-y-auto mb-4 pr-2 scrollbar-thin" data-testid="session-entries">
                      {(!activeSession.entries || activeSession.entries.length === 0) ? (
                        <p className="text-muted-foreground text-sm italic py-8 text-center">
                          {activeSession.is_active ? "Start adding notes below..." : "No entries in this session."}
                        </p>
                      ) : (
                        activeSession.entries.map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-start gap-3 py-2 px-3 rounded-sm hover:bg-muted/10 group"
                            data-testid={`session-entry-${entry.id}`}
                          >
                            <span className="text-[10px] text-muted-foreground mt-1 w-12 flex-shrink-0 font-mono">
                              {formatTime(entry.timestamp)}
                            </span>
                            <span className={`mt-0.5 flex-shrink-0 ${ENTRY_COLORS[entry.type] || 'text-foreground'}`}>
                              {ENTRY_ICONS[entry.type] || ENTRY_ICONS.note}
                            </span>
                            <p className={`text-sm flex-1 ${ENTRY_COLORS[entry.type] || 'text-foreground'}`}>
                              {entry.content}
                            </p>
                            {activeSession.is_active && (
                              <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 text-muted-foreground"
                                onClick={() => deleteEntry(entry.id)} data-testid={`delete-entry-${entry.id}`}>
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                      <div ref={entriesEndRef} />
                    </div>

                    {/* Input Area */}
                    {activeSession.is_active && (
                      <div className="flex gap-2 border-t border-border pt-4">
                        <Select value={entryType} onValueChange={setEntryType}>
                          <SelectTrigger className="w-[100px] bg-input border-border rounded-sm h-9 text-xs" data-testid="entry-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border">
                            <SelectItem value="note">Note</SelectItem>
                            <SelectItem value="dice_roll">Dice Roll</SelectItem>
                            <SelectItem value="combat_event">Combat</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={noteInput}
                          onChange={(e) => setNoteInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && addEntry()}
                          placeholder={
                            entryType === "note" ? "Add a session note..." :
                            entryType === "dice_roll" ? "e.g. Rolled 18 on Perception check" :
                            "e.g. Goblin takes 12 damage from fireball"
                          }
                          className="flex-1 bg-input border-border rounded-sm h-9"
                          data-testid="session-note-input"
                        />
                        <Button onClick={addEntry} className="bg-primary text-primary-foreground rounded-sm h-9" data-testid="add-entry-button">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="h-96 flex items-center justify-center text-muted-foreground">
                  Select a session or start a new one
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SessionLogPage;
